import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Compass, Filter } from 'lucide-react';
import { Card, CardTitle, CardDescription, Input, Button, Skeleton } from '@/components/ui';
import { KeywordTable, type KeywordRow } from '@/components/keyword';
import { api } from '@/lib/api';
import type { AlternativeKeyword } from '@keywordgod/shared';

interface DiscoverNotice {
  originalSeed: string;
  usedSeed: string;
}

interface DiscoverFilters {
  minTotalSearch: number;
  maxSaturation: number; // 포화도(%)
  minGodScore: number;
}

const DEFAULT_FILTERS: DiscoverFilters = {
  minTotalSearch: 100,
  maxSaturation: 100,
  minGodScore: 0,
};

export function Discover() {
  const [seed, setSeed] = useState('');
  const [filters, setFilters] = useState<DiscoverFilters>(DEFAULT_FILTERS);
  const [results, setResults] = useState<AlternativeKeyword[]>([]);
  const [notice, setNotice] = useState<DiscoverNotice | null>(null);

  const m = useMutation({
    mutationFn: async (
      s: string,
    ): Promise<{ items: AlternativeKeyword[]; notice: DiscoverNotice | null }> => {
      // BFS depth 2: 시드 → 연관 50개 → 각 연관의 연관 (캐시 의존 — 클라이언트에서 직렬 호출)
      const root = await api.discover.seed(s);
      const direct = root.related;
      // 깊이 2: 시간/요금제 부담을 줄이기 위해 상위 10개 정도만 추가 확장 — 캐시 히트면 빠름
      const top10 = direct.slice(0, 10);
      const expanded = await Promise.allSettled(top10.map((k) => api.discover.seed(k.keyword)));
      const flat: AlternativeKeyword[] = [...direct];
      for (const r of expanded) {
        if (r.status === 'fulfilled') flat.push(...r.value.related);
      }
      // 중복 제거 (keyword 기준)
      const map = new Map<string, AlternativeKeyword>();
      for (const k of flat) {
        const prev = map.get(k.keyword);
        if (!prev || k.godScore > prev.godScore) map.set(k.keyword, k);
      }
      const items = Array.from(map.values()).sort((a, b) => b.godScore - a.godScore);
      // Phase 27: 시드 자동 변형 알림
      const fallbackNotice: DiscoverNotice | null = root.originalSeed
        ? { originalSeed: root.originalSeed, usedSeed: root.seed }
        : null;
      return { items, notice: fallbackNotice };
    },
    onSuccess: ({ items, notice: n }) => {
      setResults(items);
      setNotice(n);
    },
  });

  const filtered = useMemo(() => {
    return results.filter(
      (r) =>
        r.totalSearch >= filters.minTotalSearch &&
        r.godScore >= filters.minGodScore,
    );
  }, [results, filters]);

  const rows: KeywordRow[] = filtered.map((r) => ({
    keyword: r.keyword,
    totalSearch: r.totalSearch,
    godScore: r.godScore,
    grade: r.grade,
  }));

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-extrabold flex items-center gap-2">
          <Compass className="h-7 w-7 text-gold" />
          <span className="text-gold-gradient">키워드 발굴</span>
        </h1>
        <p className="mt-1 text-sm text-gray-400">
          시드 키워드의 연관 키워드의 연관 키워드까지 (BFS depth 2) 끌어옵니다
        </p>
      </header>

      <Card>
        <CardTitle>시드 키워드</CardTitle>
        <CardDescription>탐색 출발점이 될 키워드를 입력하세요</CardDescription>
        <form
          className="mt-4 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (seed.trim()) m.mutate(seed.trim());
          }}
        >
          <Input
            placeholder="예: 다이어트 보조제"
            value={seed}
            onChange={(e) => setSeed(e.target.value)}
          />
          <Button type="submit" disabled={m.isPending || !seed.trim()}>
            {m.isPending ? '탐색 중…' : '발굴 시작'}
          </Button>
        </form>
      </Card>

      {(m.isPending || results.length > 0) && (
        <Card>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gold" />
              필터
            </CardTitle>
            <button
              type="button"
              onClick={() => setFilters(DEFAULT_FILTERS)}
              className="text-xs text-gray-400 hover:text-gold"
            >
              초기화
            </button>
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <FilterField
              label="최소 검색량"
              value={filters.minTotalSearch}
              onChange={(v) => setFilters((f) => ({ ...f, minTotalSearch: v }))}
              step={100}
            />
            <FilterField
              label="최대 포화도(%)"
              value={filters.maxSaturation}
              onChange={(v) => setFilters((f) => ({ ...f, maxSaturation: v }))}
              step={5}
            />
            <FilterField
              label="최소 GodScore"
              value={filters.minGodScore}
              onChange={(v) => setFilters((f) => ({ ...f, minGodScore: v }))}
              step={5}
            />
          </div>
        </Card>
      )}

      {m.isPending && (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      )}

      {results.length > 0 && !m.isPending && (
        <Card>
          <CardTitle>
            결과 — {rows.length} / {results.length}개
          </CardTitle>
          <div className="mt-4">
            <KeywordTable rows={rows} />
          </div>
        </Card>
      )}

      {notice && (
        <Card className="border-amber-400/40 bg-amber-400/10 text-amber-200">
          <div className="text-sm">
            <span className="font-bold">자동 변형:</span> 입력하신{' '}
            <code className="rounded bg-amber-400/20 px-1.5 py-0.5 font-semibold">
              {notice.originalSeed}
            </code>{' '}
            은(는) 네이버 광고 데이터에 없어{' '}
            <code className="rounded bg-amber-400/20 px-1.5 py-0.5 font-semibold">
              {notice.usedSeed}
            </code>{' '}
            으로 발굴했습니다.
          </div>
        </Card>
      )}

      {m.isError && (
        <Card className="border-red-500/40 bg-red-500/10 text-red-300">
          {(m.error as Error).message}
        </Card>
      )}
    </div>
  );
}

function FilterField({
  label,
  value,
  onChange,
  step = 1,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  step?: number;
}) {
  return (
    <label className="block">
      <span className="text-xs text-gray-400">{label}</span>
      <Input
        type="number"
        className="mt-1"
        value={value}
        step={step}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
      />
    </label>
  );
}
